import { makeDateFromString } from './helpers';
import type { Comparator } from './utils.type';

/**
 * @memberof SharedComponents
 * @class Comparator
 * @alias comparators
 */

/**
 * Compares strings 2 string using locale.
 * @memberof Comparator
 * @param {string}  a
 * @param {string} b
 * @returns {number}
 */
const compareStrings: Comparator = (a: string, b: string): number =>
    a.localeCompare(b);

/**
 * Compares date represented as strings using in formats dd.mm.yyyy and yyyy-mm-dd.
 * @memberof Comparator
 * @param {Date} a
 * @param {Date} b
 * @returns {number}
 */
const compareDates: Comparator<Date> = (a: Date, b: Date): number =>
    a.getTime() - b.getTime();

/**
 * Compares 2 strings or string-line numbers taking into account spaces.
 * @memberof Comparator
 * @param {string|number} a
 * @param {string|number} b
 * @returns {number}
 */
const compareNumbersAsStrings: Comparator<string | number> = (
    a: string | number,
    b: string | number
): number => {
    const returnValue =
        parseFloat(String(typeof a === 'string' ? a.replace(/\s+/g, '') : a)) -
        parseFloat(String(typeof b === 'string' ? b.replace(/\s+/g, '') : b));
    return !Number.isNaN(returnValue) ? returnValue : 0;
};

/**
 * Detects types and compares values of strings, numbers and dates.
 * @memberof Comparator
 * @param {string|number} a
 * @param {string|number} b
 * @returns {number}
 */
const compareValuesAsStrings: Comparator<string | number> = (
    a: string | number,
    b: string | number
): number => {
    const stringA = String(a);
    const stringB = String(b);

    if (isNaN(parseInt(stringA)) || isNaN(parseInt(stringB))) {
        return compareStrings(String(a), String(b));
    }

    // matches the hh:mm:ss part of "dd.mm.yyyy hh:mm:ss" timestamp
    const timeRegex = / (\d{2}):(\d{2}):(\d{2})$/;
    const dateA = makeDateFromString(
        timeRegex.test(stringA) ? stringA : `${a} 00:00:00`
    );
    const dateB = makeDateFromString(
        timeRegex.test(stringB) ? stringB : `${b} 00:00:00`
    );
    if (dateA instanceof Date && dateB instanceof Date) {
        return compareDates(dateA, dateB);
    }

    return compareNumbersAsStrings(a, b);
};

export {
    compareStrings,
    compareNumbersAsStrings,
    compareDates,
    compareValuesAsStrings,
};
